import React, { useEffect, useRef, useState } from 'react';

const YEAR_MIN = 1900;
const YEAR_MAX = 2100;

function clampYear(n: number): number {
  return Math.min(YEAR_MAX, Math.max(YEAR_MIN, Math.round(n)));
}

function digitsOnly(raw: string, maxLen = 4): string {
  return raw.replace(/\D/g, '').slice(0, maxLen);
}

export interface TodoNoteYearInputProps {
  year: number;
  disabled?: boolean;
  onYearCommit: (year: number) => void;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  'aria-label'?: string;
}

/** 연도: 키보드 ↑↓는 1년 단위, 숫자 직접 입력도 가능 */
const TodoNoteYearInput: React.FC<TodoNoteYearInputProps> = ({
  year,
  disabled,
  onYearCommit,
  className,
  inputRef,
  'aria-label': ariaLabel = '연도',
}) => {
  const [draft, setDraft] = useState(() => String(year));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(String(year));
  }, [year]);

  const commitDraft = (raw: string) => {
    const trimmed = digitsOnly(raw);
    if (!trimmed) {
      setDraft(String(year));
      return;
    }
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n)) {
      setDraft(String(year));
      return;
    }
    const yy = clampYear(n);
    setDraft(String(yy));
    onYearCommit(yy);
  };

  const inputClass = ['cp-todo-note-due-year-input', className].filter(Boolean).join(' ');

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className={inputClass}
      disabled={disabled}
      value={draft}
      aria-label={ariaLabel}
      onFocus={() => {
        focusedRef.current = true;
        setDraft(String(year));
      }}
      onChange={(e) => {
        setDraft(digitsOnly(e.target.value));
      }}
      onBlur={() => {
        focusedRef.current = false;
        commitDraft(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          const base = parseInt(digitsOnly(draft), 10);
          const current = Number.isFinite(base) ? base : year;
          const next = clampYear(current + (e.key === 'ArrowUp' ? 1 : -1));
          setDraft(String(next));
          onYearCommit(next);
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
          return;
        }
        e.stopPropagation();
      }}
    />
  );
};

export default TodoNoteYearInput;
